import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { useInvokeOnce } from '@heidi/utils';
import { AlertCircle, Loader2, Search, UserRound, UsersRound } from 'lucide-react';

import type { JSX } from "react";

interface PatientSearchBlockProps {
  status: 'searching' | 'completed' | 'error';
  query?: string;
  totalResults?: number;
  error?: string;
  onBlockError: (blockType: 'patient_search', error?: string) => void;
  className?: string;
}

/**
 * PatientSearchBlock - Displays patient profile search status and results summary
 *
 * Visual states:
 * - SEARCHING: Loading spinner with query context
 * - COMPLETED: Results summary with patient count
 * - ERROR: Error message
 */
export const PatientSearchBlock = ({
  status,
  query,
  totalResults,
  error,
  onBlockError,
  className,
}: PatientSearchBlockProps): JSX.Element => {
  const { t } = useTranslations();
  useInvokeOnce(() => onBlockError('patient_search', error), status === 'error');

  // Searching state
  if (status === 'searching') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background-secondary">
          <UsersRound className="size-4 text-text-tertiary" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="font-medium text-text-primary">{t('chat.patientSearch.title' as TranslationKey)}</TypographyP2>
          <div className="flex items-center gap-1.5">
            <Loader2 className="size-3 animate-spin text-text-tertiary" />
            <TypographyCaption className="min-w-0 truncate text-text-tertiary">
              {query ? t('chat.patientSearch.searchingForQuery' as TranslationKey, { query }) : t('chat.patientSearch.searching' as TranslationKey)}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
      >
        <FramedIcon size="sm" state="error" icon={<AlertCircle />} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="font-medium text-text-primary">{t('chat.patientSearch.title' as TranslationKey)}</TypographyP2>
          <TypographyCaption className="text-validation-destructive">
            {error || t('chat.patientSearch.errorDefault' as TranslationKey)}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  // Completed state
  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-border/50 bg-surface p-4',
        'transition-all duration-200 animate-in fade-in',
        className
      )}
    >
      <div
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md',
          totalResults && totalResults > 0
            ? 'bg-brand-primary/10'
            : 'bg-background-secondary'
        )}
      >
        {totalResults && totalResults > 0 ? (
          <Search className="size-4 text-brand-primary" />
        ) : (
          <UserRound className="size-4 text-text-tertiary" />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TypographyP2 className="font-medium text-text-primary">{t('chat.patientSearch.title' as TranslationKey)}</TypographyP2>
        <TypographyCaption className="text-text-tertiary">
          {totalResults !== undefined && totalResults > 0
            ? (query
                ? t('chat.patientSearch.foundCountWithQuery' as TranslationKey, { count: totalResults, query })
                : t('chat.patientSearch.foundCount' as TranslationKey, { count: totalResults }))
            : (query
                ? t('chat.patientSearch.noResultsWithQuery' as TranslationKey, { query })
                : t('chat.patientSearch.noResults' as TranslationKey))}
        </TypographyCaption>
      </div>
    </div>
  );
};
